import React, { useState } from 'react';
import DatePickerCustom from '../../../../BaseModule/Components/DatePickerCustom/DatePickerCustom';
import apiClient from '../../../../../Services/ApiClient';
import { useEffectAsync } from '../../../../../helpers';
import moment, { Moment } from 'moment';
import { CustomTimePickerTime } from '../../../../BaseModule/Components/TimePickerCustom/TimePickerCustom';

interface Props {
	onChange: (date: Moment) => void;
	onMonthChange: () => void;
	workplaceId: string;
	workplaceMemberId: string;
	workplaceCaseTypeId: string;
	handleProcessing: (key: string, isProcessing: boolean) => void;
	minDate?: string;
	maxDate?: string;
	preselectedDateTime?: string;
}

const TermDatePicker: React.FC<Props> = ({
	onChange,
	onMonthChange,
	workplaceId,
	workplaceMemberId,
	workplaceCaseTypeId,
	handleProcessing,
	minDate,
	maxDate,
	preselectedDateTime,
}) => {
	const [excludeDates, setExcludeDates] = useState<Date[]>([]);

	//Loading from API date with at least one available time and creating list of excluded dates for date picker
	const loadHighlightDates = async (dateFrom: string, dateTo: string) => {
		//Register function that is loading
		handleProcessing('datesAvailability', true);
		const result = await apiClient.get(
			`/workplaces/${workplaceId}/calendar/availability/days-range?dateFrom=${dateFrom}&dateTo=${dateTo}&workplaceMemberId=${workplaceMemberId}&workplaceCaseTypeId=${workplaceCaseTypeId}&disablePast=true`,
		);
		if (result.status === 200) {
			const tempExcludedDates: Date[] = [];
			const starDate = moment(dateFrom);
			const endDate = moment(dateTo);
			for (
				let m = moment(starDate);
				m.diff(endDate, 'days') <= 0;
				m.add(1, 'days')
			) {
				const dateInString = m.format('YYYY-MM-DD');
				if (!result.data.some((item: string) => item === dateInString)) {
					tempExcludedDates.push(new Date(dateInString));
				}
			}
			setExcludeDates(tempExcludedDates);
		} else {
			setExcludeDates([]);
		}
		//Register function that is not loading anymore
		handleProcessing('datesAvailability', false);
	};

	const getMonthRangeDates = (selectedDate: Moment) => {
		return {
			startOfMonth: selectedDate.startOf('month').format('YYYY-MM-DD'),
			endOfMonth: selectedDate.endOf('month').format('YYYY-MM-DD'),
		};
	};

	useEffectAsync(async () => {
		const { startOfMonth, endOfMonth } = getMonthRangeDates(
			moment(preselectedDateTime),
		);
		await loadHighlightDates(startOfMonth, endOfMonth);
	}, []);

	const handleOnMonthChange = async (date: Moment) => {
		const { startOfMonth, endOfMonth } = getMonthRangeDates(date);
		await loadHighlightDates(startOfMonth, endOfMonth);
		if (
			date.format('YYYY-MM-DD') !==
			moment(preselectedDateTime).format('YYYY-MM-DD')
		) {
			onMonthChange();
		}
	};

	return (
		<DatePickerCustom
			onChange={onChange}
			value={moment(preselectedDateTime).format()}
			onMonthChange={handleOnMonthChange}
			excludeDates={excludeDates}
			minDate={minDate}
			maxDate={maxDate}
		/>
	);
};

export default TermDatePicker;
